import Article from '@/components/article/article';
import {
  practitionerActions,
  practitionerSelectors,
  practitionerThunkActions,
} from '@/store/practitioner';
import {
  Button,
  Checkbox,
  IMAGE_HEIGHT,
  imageResize,
  Typography,
} from '@ecdlink/ui';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ContentConsentTypeEnum } from '@ecdlink/core';
import { contentConsentThunkActions } from '@/store/content/consent';
import { useAppDispatch } from '@/store';
import SignatureCanvas from 'react-signature-canvas';
import { PractitionerInput } from '@ecdlink/graphql';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/solid';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';

interface ReadAndAcceptAgreementProps {
  setAgreementStep: any;
  franchisorAgreementSigned?: boolean;
  coachPractitioner?: any;
}

export const ReadAndAcceptAgreement: React.FC<ReadAndAcceptAgreementProps> = ({
  setAgreementStep,
  franchisorAgreementSigned,
  coachPractitioner,
}) => {
  var practitioner = useSelector(practitionerSelectors.getPractitioner);

  if (coachPractitioner) {
    practitioner = coachPractitioner;
  }

  const [viewPermissionToShare, setViewPermissionToShare] =
    useState<boolean>(false);
  const [becomeSmartStartuser, setBecomeSmartStartuser] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const appDispatch = useAppDispatch();
  const { isOnline } = useOnlineStatus();

  useEffect(() => {
    const updateConsents = async () => {
      if (isOnline)
        await appDispatch(
          contentConsentThunkActions.getConsent({ locale: 'en-za' })
        ).unwrap();
    };

    updateConsents();
  }, []);

  const [isSigned, setIsSigned] = useState<boolean>(false);

  let signature = {} as SignatureCanvas | null;

  const saveSignature = (inputSignature: string) => {
    if (!signature?.isEmpty()) {
      const copy = Object.assign({}, practitioner);
      if (copy.signingSignature !== inputSignature) {
        imageResize(inputSignature, null, IMAGE_HEIGHT, 'image/png').then(
          (dataUrl) => {
            copy.signingSignature = dataUrl ?? null;

            const input: PractitionerInput = {
              Id: copy.id,
              IsActive: true,
              Progress: copy.progress,
              SigningSignature: copy.signingSignature,
            };

            appDispatch(practitionerActions.updatePractitioner(copy));
            appDispatch(practitionerThunkActions.updatePractitioner(input));
            setIsSigned(true);
          }
        );
      }
    }
  };

  const clearSignature = () => {
    setIsSigned(false);
    signature?.clear();
  };

  return (
    <>
      <div className="flex flex-col justify-around p-4">
        <div
          className={`${
            franchisorAgreementSigned ? 'pointer-events-none opacity-50' : ''
          }`}
        >
          {coachPractitioner ? (
            <>
              <Typography
                className={'my-3'}
                color={'textDark'}
                type={'h2'}
                text={`Give the phone to ${practitioner?.user?.firstName} & ask them to confirm each item by tapping the box:`}
              />
              <hr className="border-b-uiLight mx-2 my-2 border-dashed" />
            </>
          ) : (
            ''
          )}
          <Typography
            className={'my-3'}
            color={'textDark'}
            type={'h2'}
            text={'Read & accept the agreement'}
          />
          <Typography
            className={'my-3 w-full'}
            color={'textDark'}
            type={'h3'}
            text={
              'Please check to agree with the following and have your signature added:'
            }
          />

          <div className="'flex items-center' w-full flex-row justify-start">
            <div
              className="flex items-start gap-2"
              onClick={() => setBecomeSmartStartuser(!becomeSmartStartuser)}
            >
              <Checkbox
                checked={
                  franchisorAgreementSigned
                    ? franchisorAgreementSigned
                    : becomeSmartStartuser
                }
                onCheckboxChange={() =>
                  setBecomeSmartStartuser(!becomeSmartStartuser)
                }
              />
              <Typography
                text={`I, ${practitioner?.user?.fullName}, want to become a SmartStart franchisee so that I can help children in my community to have a brighter future.`}
                type="body"
                color="textMid"
              />
            </div>
            <div className="mt-4 flex flex-wrap items-start">
              <Checkbox<Boolean>
                checked={
                  franchisorAgreementSigned
                    ? franchisorAgreementSigned
                    : acceptTerms
                }
                onCheckboxChange={() => setAcceptTerms(!acceptTerms)}
              />
              <div className="mx-2 flex">
                <Typography
                  text={'I accept the'}
                  type="body"
                  color={'textMid'}
                  className="whitespace-nowrap"
                  onClick={() => setAcceptTerms(!acceptTerms)}
                />
                &nbsp;
                <Typography
                  onClick={() => setViewPermissionToShare(true)}
                  className={'cursor-pointer whitespace-nowrap'}
                  text={`conditions and requirements of ${' '}`}
                  underline={true}
                  type="body"
                  color={'secondary'}
                />
              </div>
              <Typography
                onClick={() => setViewPermissionToShare(true)}
                className={'cursor-pointer'}
                text={`becoming a SmartStart franchisee`}
                underline={true}
                type="body"
                color={'secondary'}
              />
            </div>
          </div>
          {coachPractitioner ? (
            <>
              <SignatureCanvas
                canvasProps={{
                  className:
                    'w-full bg-white h-60 rounded mb-4 mt-4 border border-textDark',
                }}
                ref={(ref) => {
                  signature = ref;
                }}
              />
              <div className="ml-3 flex w-full items-center justify-center">
                <XCircleIcon
                  className="text-errorMain h-11 w-11"
                  onClick={clearSignature}
                />
                <CheckCircleIcon
                  className="text-successMain h-11 w-11"
                  onClick={() => {
                    const signatureString = signature!
                      .getTrimmedCanvas()
                      .toDataURL('image/png');
                    saveSignature(signatureString);
                  }}
                />
              </div>
            </>
          ) : (
            ''
          )}
        </div>
        <div className="mt-4 -mb-4 h-full w-full self-end">
          <Button
            size="normal"
            className="mb-4 w-full"
            type="filled"
            color="primary"
            text="Next"
            textColor="white"
            icon="ArrowCircleRightIcon"
            onClick={() => setAgreementStep('programmeTypeAgreement')}
            disabled={
              !becomeSmartStartuser ||
              !acceptTerms ||
              (coachPractitioner && !isSigned)
            }
          />
        </div>
      </div>
      <Article
        visible={viewPermissionToShare}
        consentEnumType={ContentConsentTypeEnum.FranchiseeAgreement}
        onClose={function (): void {
          setViewPermissionToShare(false);
        }}
      />
    </>
  );
};
